import { useEffect } from 'react';
import { Checkbox, DatePicker, Form, Input, Modal, Select, message } from 'antd';
import { useMutation } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { createTeamSubstitution } from '@/services/teamAssignments.service';
import { FUNCTIONS, personName } from '@/components/team-assignments/model';

/**
 * Cover while a team member is away (leave, sick leave): from the first day
 * the substitute helps on all their clients in the ticked types of work; the
 * day after the last day it ends by itself, access included.
 */
export default function SubstitutionDialog({ open, absent, people, byFn, onClose, onDone }) {
  const { t } = useTranslation('teamAssignments');
  const [form] = Form.useForm();

  useEffect(() => {
    if (!open) return;
    form.resetFields();
    form.setFieldsValue({ functions: FUNCTIONS.filter((f) => byFn?.[f]), range: [dayjs(), null] });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = useMutation({
    mutationFn: (v) =>
      createTeamSubstitution({
        absentEmployeeId: absent._id,
        substituteEmployeeId: v.substitute,
        functions: v.functions,
        from: v.range[0].format('YYYY-MM-DD'),
        until: v.range[1].format('YYYY-MM-DD'),
        note: v.note || '',
      }),
    onSuccess: () => {
      message.success(t('messages.saved'));
      onDone?.();
      onClose();
    },
    onError: (e) => message.error(e?.response?.data?.message || e.message),
  });

  return (
    <Modal
      open={open}
      title={t('cover.title', { name: personName(absent) })}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText={t('actions.save')}
      cancelText={t('actions.cancel')}
      confirmLoading={save.isPending}
      okButtonProps={{ shape: 'round' }}
      cancelButtonProps={{ shape: 'round' }}
      destroyOnClose
    >
      <p style={{ opacity: 0.7, marginTop: 0 }}>{t('cover.hint')}</p>
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}>
        <Form.Item name="substitute" label={t('cover.substitute')} rules={[{ required: true, message: t('assign.pickPerson') }]}>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder={t('assign.pickPerson')}
            options={people.filter((p) => p._id !== absent?._id && !p.left).map((p) => ({ value: p._id, label: personName(p) }))}
          />
        </Form.Item>
        <Form.Item
          name="range"
          label={t('cover.dates')}
          rules={[{ required: true, validator: (_, v) => (v?.[0] && v?.[1] ? Promise.resolve() : Promise.reject(new Error(t('cover.pickDates')))) }]}
        >
          <DatePicker.RangePicker format="DD.MM.YYYY" disabledDate={(d) => d && d.isBefore(dayjs(), 'day')} />
        </Form.Item>
        <Form.Item name="functions" label={t('cover.functions')} rules={[{ required: true, type: 'array', min: 1, message: t('cover.pickFunctions') }]}>
          <Checkbox.Group>
            {FUNCTIONS.map((f) => (
              <Checkbox key={f} value={f} disabled={!byFn?.[f]}>
                {t(`functions.${f}`)}
                {byFn?.[f] ? ` (${byFn[f]})` : ''}
              </Checkbox>
            ))}
          </Checkbox.Group>
        </Form.Item>
        <Form.Item name="note" label={t('assign.note')}>
          <Input maxLength={300} placeholder={t('cover.notePlaceholder')} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
